import type { QueryClient } from '@tanstack/react-query';
import type { MailApi } from '@/api/contract';
import { ApiError } from '@/api/http';
import { queryKeys } from '@/api/queryKeys';
import type { Label, LabelMigrateResult } from '@/api/types';
import { applyCachePatches } from '@/cache/patches';

/**
 * The rename/delete loop (plan/17 §4, `POST …/labels/{id}/migrate`).
 *
 * A rename or a delete rewrites a keyword on every message that holds it, and
 * the server keeps no mailbox password to do that on its own: each step runs
 * on the caller's lease. So the client drives it — the tab that renamed or
 * deleted, and any tab that loads the list and finds a label not `active` —
 * one bounded step at a time until the server says `done`.
 *
 * **One loop per label**, whoever asks: the sidebar of each mailbox, the
 * dialog that started it and a second render of either all call `startLabelMigration`,
 * and only the first one runs. Progress lives here, outside React, so every
 * component that shows it reads the same numbers (`useLabelMigration`).
 */

export interface LabelMigration {
  accountId: string;
  labelId: string;
  /** What the loop is doing, from the row it last saw. */
  kind: 'renaming' | 'deleting';
  /** The label's name as last seen, for "Removing Work…". */
  name: string;
  /** Messages rewritten so far. */
  migrated: number;
  /** Folders a rename could not write the new keyword into (they keep the old one). */
  foldersRefused: string[];
  /** Set when a step failed; the loop has stopped and `retry` starts it again. */
  error?: string;
  /** The keywords being rewritten away from, for the cache once it is done. */
  keyword: string;
  previousKeyword: string | null;
}

const running = new Map<string, LabelMigration>();
const listeners = new Set<() => void>();
/** A snapshot per label, so `useSyncExternalStore` sees a stable value. */
let version = 0;

function emit(): void {
  version += 1;
  for (const listener of [...listeners]) listener();
}

export function subscribeLabelMigrations(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function labelMigrationsVersion(): number {
  return version;
}

export function labelMigration(labelId: string): LabelMigration | undefined {
  return running.get(labelId);
}

/** Every loop of one account, for the section's summary line. */
export function labelMigrationsOf(accountId: string): LabelMigration[] {
  return [...running.values()].filter((migration) => migration.accountId === accountId);
}

/** Tests only: forget every loop. */
export function resetLabelMigrations(): void {
  running.clear();
  emit();
}

/** Writes one label row into the cached list (or takes it out). */
function patchList(client: QueryClient, accountId: string, labelId: string, next: Label | null): void {
  client.setQueryData<Label[]>(queryKeys.account(accountId).labels(), (current) => {
    if (!current) return current;
    if (next === null) return current.filter((label) => label.id !== labelId);
    return current.map((label) => (label.id === labelId ? next : label));
  });
}

function failureCopy(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isOffline) return 'You’re offline; this will carry on when you’re back.';
    return error.message;
  }
  return 'The mail server did not answer.';
}

/**
 * Starts the loop for one label unless it is already running. Resolves when
 * the loop stops — done, or failed (the failure is recorded, not thrown).
 */
export function startLabelMigration(
  client: QueryClient,
  api: MailApi,
  accountId: string,
  label: Pick<Label, 'id' | 'name' | 'state' | 'migratedCount' | 'keyword' | 'previousKeyword'>,
): Promise<void> {
  const existing = running.get(label.id);
  if (existing && existing.error === undefined) return Promise.resolve();
  const state: LabelMigration = {
    accountId,
    labelId: label.id,
    kind: label.state === 'deleting' ? 'deleting' : 'renaming',
    name: label.name,
    migrated: label.migratedCount,
    foldersRefused: [],
    keyword: label.keyword,
    previousKeyword: label.previousKeyword,
  };
  running.set(label.id, state);
  emit();
  return loop(client, api, state);
}

async function loop(client: QueryClient, api: MailApi, state: LabelMigration): Promise<void> {
  for (;;) {
    let result: LabelMigrateResult;
    try {
      result = await api.labels.migrate(state.accountId, state.labelId);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        // Gone already: another tab finished the delete.
        running.delete(state.labelId);
        patchList(client, state.accountId, state.labelId, null);
        emit();
        return;
      }
      running.set(state.labelId, { ...state, error: failureCopy(error) });
      emit();
      return;
    }
    const current = running.get(state.labelId);
    // Forgotten mid-step (a test reset); nothing to report to.
    if (current === undefined) return;
    Object.assign(state, {
      migrated: result.label?.migratedCount ?? state.migrated + result.rewritten,
      foldersRefused: [...new Set([...state.foldersRefused, ...result.foldersRefused])],
      ...(result.label ? { name: result.label.name } : {}),
    });
    patchList(client, state.accountId, state.labelId, result.label);
    if (result.done) {
      running.delete(state.labelId);
      emit();
      finishInCache(client, state, result.label);
      // The filters may have been rewritten (§7), and the rows' keywords
      // certainly were: what is cached of either is stale now.
      void client.invalidateQueries({ queryKey: queryKeys.account(state.accountId).labels() });
      void client.invalidateQueries({ queryKey: queryKeys.account(state.accountId).sieveRules() });
      void client.invalidateQueries({ queryKey: queryKeys.account(state.accountId).sieveBackups() });
      return;
    }
    running.set(state.labelId, { ...state });
    emit();
  }
}

/**
 * The cached messages, once the server has rewritten the real ones: a
 * finished delete takes the keywords off every cached row, a finished rename
 * moves them to the new keyword. Without it the chips of a renamed label
 * would vanish from the rows on screen until each list was refetched, since
 * the old keyword stops naming the label the moment `previousKeyword` clears.
 */
function finishInCache(client: QueryClient, state: LabelMigration, label: Label | null): void {
  if (label === null) {
    const remove = [state.keyword, state.previousKeyword].filter((k): k is string => k !== null);
    applyCachePatches(client, [{ kind: 'message-labels', accountId: state.accountId, edit: { remove } }]);
    return;
  }
  // A folder that refused the new keyword leaves the old one as an alias,
  // still this label: nothing to move.
  if (state.previousKeyword === null || label.previousKeyword !== null) return;
  applyCachePatches(client, [
    {
      kind: 'message-labels',
      accountId: state.accountId,
      edit: { rename: { from: state.previousKeyword, to: label.keyword } },
    },
  ]);
}
